import React, { useEffect, useState } from 'react';
import { CircularProgress, Tooltip } from '@mui/material';

import { getStudentHistory } from '../../actions/journal';
import { formatDate } from '../../utils/journalFormat';

// История одного слушателя на вкладку: в листе журнала мышь проходит по фамилиям
// десятки раз, и каждый раз ходить на сервер незачем.
const cache = new Map();

// Тот же запрос для встроенной истории: она открывается там, где подсказки
// по наведению нет, но кэш у них общий.
const pending = new Map();

const STATUS_MARK = {
  'Учится': '•',
  'Переведён': '→',
  'Отчислен': '×',
};

const renderHistory = (rows) => (
  <div style={{ lineHeight: 1.6 }}>
    <div style={{ fontWeight: 600, marginBottom: 4 }}>Перемещения</div>
    {rows.map((row) => (
      <div key={row.id}>
        {STATUS_MARK[row.status] || '•'} {row.group_name} · {formatDate(row.date_from)}
        {row.date_to ? ` — ${formatDate(row.date_to)}` : ' — сейчас'}
        {row.hours_carried_over > 0 && ` · принесено ${row.hours_carried_over} ч`}
        {row.comment && (
          <div style={{ opacity: 0.8, paddingLeft: 12 }}>{row.comment}</div>
        )}
      </div>
    ))}
  </div>
);

/**
 * История перемещений при наведении на фамилию.
 *
 * Требование с встречи: преподаватель должен видеть, откуда слушатель пришёл,
 * не открывая карточку — карточка большая и уводит со страницы журнала.
 *
 * Запрос уходит только при первом наведении: грузить историю всей группы заранее
 * означало бы 15 лишних запросов на открытие листа.
 */
const StudentHistoryTooltip = ({ studentId, children }) => {
  const [content, setContent] = useState(cache.get(studentId) || null);

  const handleOpen = () => {
    if (content || cache.has(studentId)) return;

    cache.set(studentId, null);

    getStudentHistory(studentId)
      .then((rows) => {
        const node = rows.length === 0 ? 'Перемещений не было' : renderHistory(rows);
        cache.set(studentId, node);
        setContent(node);
      })
      .catch(() => {
        cache.delete(studentId);
        setContent('Не удалось загрузить историю');
      });
  };

  return (
    <Tooltip
      arrow
      placement="right"
      enterDelay={400}
      onOpen={handleOpen}
      title={content || <CircularProgress size={14} color="inherit" />}
    >
      {children}
    </Tooltip>
  );
};

const loadHistory = (studentId) => {
  if (cache.get(studentId)) return Promise.resolve(cache.get(studentId));
  if (pending.has(studentId)) return pending.get(studentId);

  const request = getStudentHistory(studentId)
    .then((rows) => {
      const node = rows.length === 0 ? 'Перемещений не было' : renderHistory(rows);
      cache.set(studentId, node);
      return node;
    })
    .catch(() => 'Не удалось загрузить историю')
    .finally(() => pending.delete(studentId));

  pending.set(studentId, request);
  return request;
};

/**
 * История перемещений прямо в разметке — для телефона, где навести мышь не на что,
 * а долгое нажатие на фамилию никто не угадает.
 */
export const StudentHistoryInline = ({ studentId }) => {
  const [content, setContent] = useState(cache.get(studentId) || null);

  useEffect(() => {
    let alive = true;
    loadHistory(studentId).then((node) => { if (alive) setContent(node); });
    return () => { alive = false; };
  }, [studentId]);

  return (
    <div className="journal-muted" style={{ fontSize: 12 }}>
      {content || <CircularProgress size={14} color="inherit" />}
    </div>
  );
};

export default StudentHistoryTooltip;
